import { Badge } from "@/components/ui/badge";
import { ExternalLink as ExternalLinkIcon } from "lucide-react";
import { ExternalLink } from "@/components/ui/external-link";
import { cn } from "@/lib/utils";

/**
 * Notable open source project data
 */
type NotableProject = {
  name: string;
  url: string;
  description: string;
  contributionSummary: string;
};

/**
 * Well-known open source projects with significant (non-docs) contributions.
 * These are industry-standard tools that hiring managers will recognize.
 * Ordered by GitHub stars descending.
 */
const NOTABLE_PROJECTS: NotableProject[] = [
  {
    name: "OpenClaw",
    url: "https://github.com/openclaw/openclaw",
    description: "AI agent platform and automation framework",
    contributionSummary: "Fixed transcript path handling for subagent sessions",
  },
  {
    name: "Next.js",
    url: "https://github.com/vercel/next.js",
    description: "The React framework for production",
    contributionSummary: "Fixed TypeScript type errors in example code",
  },
  {
    name: "Material UI",
    url: "https://github.com/mui/material-ui",
    description: "React components for faster development",
    contributionSummary: "Fixed React key duplication in TablePagination",
  },
  {
    name: "Home Assistant",
    url: "https://github.com/home-assistant/core",
    description: "Open source home automation platform",
    contributionSummary: "Added RF device features for smart home automation",
  },
  {
    name: "Playwright",
    url: "https://github.com/microsoft/playwright",
    description: "Cross-browser testing framework",
    contributionSummary: "Updated CI documentation to latest versions",
  },
  {
    name: "ClickHouse",
    url: "https://github.com/ClickHouse/ClickHouse",
    description: "Real-time analytics database management system",
    contributionSummary:
      "Added a configurable syslog program name and improved contributor docs",
  },
  {
    name: "LocalStack",
    url: "https://github.com/localstack/localstack",
    description: "Local AWS cloud stack emulator",
    contributionSummary: "Improved type safety of Docker orchestration",
  },
  {
    name: "Pandoc",
    url: "https://github.com/jgm/pandoc",
    description: "Universal document converter",
    contributionSummary: "Added tests for EPUB cover image extraction",
  },
  {
    name: "Carbon",
    url: "https://github.com/carbon-app/carbon",
    description: "Beautiful images of your source code",
    contributionSummary: "Added SVG export for high-quality code snippets",
  },
  {
    name: "InfluxDB",
    url: "https://github.com/influxdata/influxdb",
    description: "Scalable time-series database",
    contributionSummary: "Updated documentation for project rename",
  },
  {
    name: "TanStack Table",
    url: "https://github.com/TanStack/table",
    description: "Headless UI for building tables",
    contributionSummary: "Fixed broken documentation links",
  },
  {
    name: "Angular CLI",
    url: "https://github.com/angular/angular-cli",
    description: "CLI for Angular applications",
    contributionSummary: "Fixed content hash generation in production builds",
  },
  {
    name: "Appium",
    url: "https://github.com/appium/appium",
    description: "Mobile app automation framework",
    contributionSummary: "Added iOS Safari troubleshooting documentation",
  },
  {
    name: "typescript-eslint",
    url: "https://github.com/typescript-eslint/typescript-eslint",
    description: "TypeScript tooling for ESLint",
    contributionSummary: "Improved ban-types docs for empty object typing",
  },
  {
    name: "Gotify",
    url: "https://github.com/gotify/server",
    description: "Self-hosted push notification server",
    contributionSummary: "Fixed accessibility and form validation issues",
  },
  {
    name: "Notesnook",
    url: "https://github.com/streetwriters/notesnook",
    description: "Privacy-focused note-taking app",
    contributionSummary: "Clarified sync option descriptions for data safety",
  },
  {
    name: "nock",
    url: "https://github.com/nock/nock",
    description: "HTTP mocking library for Node.js",
    contributionSummary: "Improved error output for debugging test failures",
  },
  {
    name: "MapLibre GL JS",
    url: "https://github.com/maplibre/maplibre-gl-js",
    description: "Interactive vector maps for the web",
    contributionSummary:
      "Added a verified Next.js setup with an example app, fixing silently blank maps",
  },
  {
    name: "WebdriverIO",
    url: "https://github.com/webdriverio/webdriverio",
    description: "Browser automation framework",
    contributionSummary: "Multiple features, CI fixes, and API improvements",
  },
  {
    name: "react-map-gl",
    url: "https://github.com/visgl/react-map-gl",
    description: "React components for MapLibre and Mapbox maps",
    contributionSummary: "Enabled bundlers to remove unused map components",
  },
  {
    name: "JUnit",
    url: "https://github.com/junit-team/junit-framework",
    description: "Testing framework for Java and the JVM",
    contributionSummary:
      "Co-authored fix for `NamespacedHierarchicalStore#computeIfAbsent`, preserving deferred behavior and making recursive updates safe",
  },
  {
    name: "Spotless",
    url: "https://github.com/diffplug/spotless",
    description: "Code formatter for Java and more",
    contributionSummary: "Fixed kotlinGradle target usage in documentation",
  },
  {
    name: "Chrono",
    url: "https://github.com/wanasit/chrono",
    description: "Natural language date parser for JavaScript",
    contributionSummary:
      'Added parsing for end-of-period dates such as "end of next month"',
  },
  {
    name: "OpenRewrite",
    url: "https://github.com/openrewrite/rewrite",
    description: "Automated large-scale code refactoring",
    contributionSummary:
      "Fixed nine refactoring recipes that could flip logic, corrupt output, or break builds",
  },
  {
    name: "Spring Batch",
    url: "https://github.com/spring-projects/spring-batch",
    description: "Enterprise batch processing framework",
    contributionSummary:
      "Migrated tests to Testcontainers, improved type safety",
  },
  {
    name: "AssertJ",
    url: "https://github.com/assertj/assertj",
    description: "Fluent assertions for Java",
    contributionSummary: "Updated CI workflows to Java 21 LTS",
  },
  {
    name: "NativeScript",
    url: "https://github.com/NativeScript/nativescript-cli",
    description: "Cross-platform mobile development",
    contributionSummary: "Fixed proxy handling for local development",
  },
  {
    name: "Vaadin",
    url: "https://github.com/vaadin/flow",
    description: "Java framework for building web applications",
    contributionSummary:
      "Fixed Unicode classpath resource handling in Spring builds",
  },
  {
    name: "PostHog",
    url: "https://github.com/PostHog/posthog-js",
    description: "Product analytics and session replay SDK",
    contributionSummary:
      "Stopped duplicate extension scripts behind relative API proxies",
  },
  {
    name: "Eclipse JDT",
    url: "https://github.com/eclipse-jdt/eclipse.jdt.core",
    description: "The Java compiler behind Eclipse and JVM tooling",
    contributionSummary:
      "Fixed four compiler defects: three internal errors and a nested-lambda memory blowup, all with regression tests",
  },
];

type NotableProjectsHighlightProps = {
  className?: string;
};

/**
 * Hero section highlighting notable open source contributions
 */
export function NotableProjectsHighlight({
  className = "",
}: NotableProjectsHighlightProps) {
  return (
    <section className={cn("space-y-4", className)}>
      <div className="space-y-2">
        <h2 className="text-xl font-semibold sm:text-2xl">
          Contributing to Industry-Standard Tools
        </h2>
        <p className="text-muted-foreground text-base">
          Code contributions merged into widely-used open source projects,
          downloaded millions of times weekly by developers worldwide.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {NOTABLE_PROJECTS.map((project) => (
          <ExternalLink
            key={project.name}
            href={project.url}
            className="group"
            aria-label={`${project.name}: ${project.description}`}
          >
            <Badge
              variant="secondary"
              className="dark:bg-secondary dark:hover:bg-secondary/80 cursor-pointer bg-[rgba(0,0,0,0.05)] px-3 py-1 text-base transition-colors hover:bg-[rgba(0,0,0,0.08)] supports-[text-box:trim-both_cap_alphabetic]:py-[6px]"
            >
              <span>{project.name}</span>
              <ExternalLinkIcon
                className="ml-1 size-4 opacity-60 transition-opacity group-hover:opacity-100"
                aria-hidden="true"
              />
            </Badge>
          </ExternalLink>
        ))}
      </div>
    </section>
  );
}
